import * as React from 'react'
import {observer} from 'mobx-react'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {OptionItem, sortOptions} from '../../../../lib/helpers'
import {ErrorMessage} from '../../../ui/ErrorMessage'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {FormPart} from '../../../wrappers/FormPart'
import {InputWrapper} from '../../../wrappers/InputWrapper'
import {SelectWrapper} from '../../../wrappers/SelectWrapper'
import {DirtyWrapper} from '../../../wrappers/DirtyWrapper'

///////////////////////////////////////////////////////////////////////////////

export const PaymentAddressForm = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  let form = api.paymentForm
  let formData = form.data
  let states = form.getStates()
  let countries = api.resolveConfig('countries')
  let priorityCountries = api.resolveConfig('priorityCountries')

  const countriesOptions = countries.map((country) => {
    return {
      value: country,
      label: api.trans(`countries.${country}`, {defaultValue: country}),
    }
  })

  const sortedCountriesOptions = sortOptions(
    countriesOptions,
    priorityCountries,
  )

  let errors = form.validationErrors
  let street = formData.stored_customer_street || ''
  let streetNumber = formData.stored_customer_street_number || ''
  let street2 = formData.stored_customer_street2 || ''
  let pobox = formData.stored_customer_pobox || ''
  let zipCode = formData.stored_customer_zip_code || ''
  let city = formData.stored_customer_city || ''
  let country = formData.stored_customer_country || ''
  let state = formData.stored_customer_state || ''

  let showStoredCustomerStreet = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerStreet',
  )
  let showStoredCustomerStreetNumber = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerStreetNumber',
  )
  let showStoredCustomerStreet2 = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerStreet2',
  )
  let showStoredCustomerPobox = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerPobox',
  )
  let showStoredCustomerZipCode = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerZipCode',
  )
  let showStoredCustomerCity = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerCity',
  )
  let showStoredCustomerCountry = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerCountry',
  )
  let showStoredCustomerState = form.showStoredCustomerState()

  let setStreet = (value: string) => (formData.stored_customer_street = value)
  let setStreetNumber = (value: string) =>
    (formData.stored_customer_street_number = value)
  let setStreet2 = (value: string) => (formData.stored_customer_street2 = value)
  let setPobox = (value: string) => (formData.stored_customer_pobox = value)
  let setZipCode = (value: string) =>
    (formData.stored_customer_zip_code = value)
  let setCity = (value: string) => (formData.stored_customer_city = value)
  let setCountry = (value: string) => (formData.stored_customer_country = value)
  let setState = (value: string) => (formData.stored_customer_state = value)

  let submit = () => form.validateAndSend()

  return (
    <fieldset>
      <legend>{api.trans('blocks.payment_address.legend')}</legend>

      <FormPart className="payment-address-form" onSubmit={submit}>
        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_START} form={form} />

        {showStoredCustomerStreet && (
          <div className="form-block stored_customer_street">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_street',
                )}
              >
                {api.trans('payment_form.stored_customer_street')}
              </label>
              <InputWrapper form={form} fields="stored_customer_street">
                <DirtyWrapper form={form} fields="stored_customer_street">
                  <input
                    type="text"
                    id={api.ui.generateUniqueElementId(
                      'stored_customer_street',
                    )}
                    aria-describedby={api.ui.generateUniqueElementId(
                      'stored_customer_street-error',
                    )}
                    className="input-text"
                    name="stored_customer_street"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    autoComplete="address-line1"
                    autoCapitalize="words"
                    placeholder={api.trans(
                      'payment_form.placeholders.stored_customer_street',
                    )}
                  />
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_street"
              id={api.ui.generateUniqueElementId(
                'stored_customer_street-error',
              )}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_1} form={form} />

        {showStoredCustomerStreetNumber && (
          <div className="form-block stored_customer_street_number">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_street_number',
                )}
              >
                <span>
                  {api.trans('payment_form.stored_customer_street_number')}
                </span>
              </label>
              <InputWrapper form={form} fields="stored_customer_street_number">
                <DirtyWrapper
                  form={form}
                  fields="stored_customer_street_number"
                >
                  <input
                    type="text"
                    id={api.ui.generateUniqueElementId(
                      'stored_customer_street_number',
                    )}
                    aria-describedby={api.ui.generateUniqueElementId(
                      'stored_customer_street_number-error',
                    )}
                    className="input-text"
                    name="stored_customer_street_number"
                    value={streetNumber}
                    onChange={(e) => setStreetNumber(e.target.value)}
                    placeholder={api.trans(
                      'payment_form.placeholders.stored_customer_street_number',
                    )}
                  />
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_street_number"
              id={api.ui.generateUniqueElementId(
                'stored_customer_street_number-error',
              )}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_2} form={form} />

        {showStoredCustomerStreet2 && (
          <div className="form-block stored_customer_street2">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_street2',
                )}
              >
                {api.trans('payment_form.stored_customer_street2')}
              </label>
              <InputWrapper form={form} fields="stored_customer_street2">
                <DirtyWrapper form={form} fields="stored_customer_street2">
                  <input
                    type="text"
                    id={api.ui.generateUniqueElementId(
                      'stored_customer_street2',
                    )}
                    aria-describedby={api.ui.generateUniqueElementId(
                      'stored_customer_street2-error',
                    )}
                    className="input-text"
                    name="stored_customer_street2"
                    value={street2}
                    onChange={(e) => setStreet2(e.target.value)}
                    autoComplete="address-line2"
                    autoCapitalize="words"
                    placeholder={api.trans(
                      'payment_form.placeholders.stored_customer_street2',
                    )}
                  />
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_street2"
              id={api.ui.generateUniqueElementId(
                'stored_customer_street2-error',
              )}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_3} form={form} />

        {showStoredCustomerPobox && (
          <div className="form-block stored_customer_pobox">
            <div className="field-block">
              <InputWrapper form={form} fields="stored_customer_pobox">
                <DirtyWrapper form={form} fields="stored_customer_pobox">
                  <input
                    type="text"
                    id={api.ui.generateUniqueElementId('stored_customer_pobox')}
                    aria-describedby={api.ui.generateUniqueElementId(
                      'stored_customer_pobox-error',
                    )}
                    className="input-text"
                    name="stored_customer_pobox"
                    value={pobox}
                    onChange={(e) => setPobox(e.target.value)}
                    placeholder={api.trans(
                      'payment_form.placeholders.stored_customer_pobox',
                    )}
                  />
                </DirtyWrapper>
              </InputWrapper>
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_pobox',
                )}
              >
                {api.trans('payment_form.stored_customer_pobox')}
              </label>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_pobox"
              id={api.ui.generateUniqueElementId('stored_customer_pobox-error')}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_4} form={form} />

        {showStoredCustomerZipCode && (
          <div className="form-block stored_customer_zip_code">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_zip_code',
                )}
              >
                {api.trans('payment_form.stored_customer_zip_code')}
              </label>
              <InputWrapper form={form} fields="stored_customer_zip_code">
                <DirtyWrapper form={form} fields="stored_customer_zip_code">
                  <input
                    type="text"
                    id={api.ui.generateUniqueElementId(
                      'stored_customer_zip_code',
                    )}
                    aria-describedby={api.ui.generateUniqueElementId(
                      'stored_customer_zip_code-error',
                    )}
                    className="input-text"
                    name="stored_customer_zip_code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    autoComplete="postal-code"
                    placeholder={api.trans(
                      'payment_form.placeholders.stored_customer_zip_code',
                    )}
                  />
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_zip_code"
              id={api.ui.generateUniqueElementId(
                'stored_customer_zip_code-error',
              )}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_5} form={form} />

        {showStoredCustomerCity && (
          <div className="form-block stored_customer_city">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId('stored_customer_city')}
              >
                {api.trans('payment_form.stored_customer_city')}
              </label>
              <InputWrapper form={form} fields="stored_customer_city">
                <DirtyWrapper form={form} fields="stored_customer_city">
                  <input
                    type="text"
                    id={api.ui.generateUniqueElementId('stored_customer_city')}
                    aria-describedby={api.ui.generateUniqueElementId(
                      'stored_customer_city-error',
                    )}
                    className="input-text"
                    name="stored_customer_city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    autoComplete="address-level2"
                    placeholder={api.trans(
                      'payment_form.placeholders.stored_customer_city',
                    )}
                  />
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_city"
              id={api.ui.generateUniqueElementId('stored_customer_city-error')}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_6} form={form} />

        {showStoredCustomerCountry && (
          <div className="form-block stored_customer_country">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_country',
                )}
              >
                {api.trans('payment_form.stored_customer_country')}
              </label>
              <InputWrapper form={form} fields="stored_customer_country">
                <DirtyWrapper form={form} fields="stored_customer_country">
                  <SelectWrapper>
                    <select
                      className="input-select"
                      id={api.ui.generateUniqueElementId(
                        'stored_customer_country',
                      )}
                      aria-describedby={api.ui.generateUniqueElementId(
                        'stored_customer_country-error',
                      )}
                      value={country}
                      name="stored_customer_country"
                      onChange={(e) => setCountry(e.target.value)}
                      autoComplete="country"
                    >
                      <option value="" disabled={true}>
                        {api.trans(
                          'payment_form.placeholders.stored_customer_country',
                        )}
                      </option>

                      {sortedCountriesOptions.map((country, key) => (
                        <option key={key} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </SelectWrapper>
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_country"
              id={api.ui.generateUniqueElementId(
                'stored_customer_country-error',
              )}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_7} form={form} />

        {showStoredCustomerState && (
          <div className="form-block stored_customer_state">
            <div className="field-block">
              <label
                className="label-block"
                htmlFor={api.ui.generateUniqueElementId(
                  'stored_customer_state',
                )}
              >
                {api.trans(`payment_form.stored_customer_state.${country}`, {
                  defaultValue: api.trans(
                    'payment_form.stored_customer_state.default',
                    {
                      defaultValue: null,
                    },
                  ),
                })}
              </label>
              <InputWrapper form={form} fields="stored_customer_state">
                <DirtyWrapper form={form} fields="stored_customer_state">
                  <SelectWrapper>
                    <select
                      className="input-select"
                      id={api.ui.generateUniqueElementId(
                        'stored_customer_state',
                      )}
                      aria-describedby={api.ui.generateUniqueElementId(
                        'stored_customer_state-error',
                      )}
                      value={state}
                      name="stored_customer_state"
                      onChange={(e) => setState(e.target.value)}
                      autoComplete="address-level1"
                    >
                      <option value="" disabled={true}>
                        {api.trans(
                          `payment_form.placeholders.stored_customer_state.${country}`,
                          {
                            defaultValue: api.trans(
                              'payment_form.placeholders.stored_customer_state.default',
                              {
                                defaultValue: null,
                              },
                            ),
                          },
                        )}
                      </option>

                      {states.map((state, key) => (
                        <option key={key} value={state}>
                          {api.trans(`states.${country}.${state}`, {
                            defaultValue: state,
                          })}
                        </option>
                      ))}
                    </select>
                  </SelectWrapper>
                </DirtyWrapper>
              </InputWrapper>
            </div>

            <ErrorMessage
              errors={errors}
              errorKey="stored_customer_state"
              id={api.ui.generateUniqueElementId('stored_customer_state-error')}
            />
          </div>
        )}

        <DynamicSlot name={DynamicSlotName.ADDRESS_FORM_END} form={form} />
      </FormPart>
    </fieldset>
  )
})
